import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KitDownloadModal({
  open,
  onClose,
  kitName = "Filastrocche delle ditina",
  downloadFileName = "Filastrocche-delle-ditina-Teresa-Vilardo.pdf",
  downloadUrl = null,
  resourceId = null,
  title = "Filastrocche",
  titleLine2 = "delle ditina",
  subtitle = "Il tuo regalo",
  intro = "Compila il modulo per ricevere il PDF 💌",
}) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    privacy_accepted: false,
    marketing_consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [downloadReady, setDownloadReady] = useState(null);
  const [successQuote, setSuccessQuote] = useState("");

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accepted) {
      toast.error("Devi accettare il trattamento dei dati per procedere.");
      return;
    }
    setLoading(true);
    try {
      // Prefer new resource endpoint when resourceId provided, else legacy /kit-request
      const endpoint = resourceId ? `${API}/resources/${resourceId}/download` : `${API}/kit-request`;
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          kit_name: kitName,
          download_url: downloadUrl || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Errore durante l'invio.");
      }
      const data = await res.json();
      const finalUrl = downloadUrl || data.download_url;
      setSuccessQuote(data.success_quote || "");
      // Show success screen with download button
      setDownloadReady(finalUrl);
    } catch (err) {
      toast.error(err.message || "Qualcosa è andato storto. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDownloadReady(null);
    setForm({ first_name: "", last_name: "", email: "", phone: "", privacy_accepted: false, marketing_consent: false });
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-[#3D2140]/70 backdrop-blur-sm overflow-y-auto"
          onClick={handleClose}
          data-testid="kit-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg bg-[#FAF8F5] rounded-[24px] md:rounded-[32px] p-6 md:p-10 shadow-2xl my-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="kit-modal"
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#3D2140]/5 transition-colors"
              aria-label="Chiudi"
              data-testid="kit-modal-close"
            >
              <X size={20} className="text-[#3D2140]" />
            </button>

            {downloadReady ? (
              <div className="text-center py-4" data-testid="kit-success">
                <div className="h-16 w-16 mx-auto rounded-full bg-[#FF2768]/12 flex items-center justify-center mb-5">
                  <Download size={28} className="text-[#FF2768]" />
                </div>
                <h4 className="font-display text-3xl md:text-4xl text-[#3D2140] leading-tight mb-3">
                  La tua risorsa è pronta 💗
                </h4>
                <p className="text-[#3D2140]/70 leading-relaxed mb-6">
                  Grazie per essere entrata/o nella <em className="text-[#FF2768] not-italic font-semibold">Biblioteca T.A.T.A.</em>
                </p>
                {successQuote && (
                  <blockquote className="italic font-editorial text-lg text-[#3D2140] border-l-4 border-[#FF2768] pl-4 mx-auto max-w-md text-left mb-8">
                    "{successQuote}"
                  </blockquote>
                )}
                <a
                  href={downloadReady}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={downloadFileName}
                  onClick={() => setTimeout(handleClose, 600)}
                  data-testid="kit-download-final"
                  className="inline-flex items-center justify-center gap-3 bg-[#FF2768] text-white rounded-full px-8 py-4 font-semibold text-base shadow-lg hover:shadow-xl hover:bg-[#E01F5A] transition-all"
                >
                  <Download size={18} />
                  Scarica il PDF
                </a>
              </div>
            ) : (
            <>
            <div className="mb-6">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-3 font-semibold">— {subtitle}</p>
              <h3 className="font-display text-4xl md:text-5xl text-[#3D2140] leading-[0.95] mb-2">
                {title}<br/>{titleLine2}
              </h3>
              <p className="text-sm text-[#3D2140]/70 mt-3 leading-relaxed">
                {intro}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="kit-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="first_name" className="block text-xs font-semibold text-[#3D2140] mb-1.5">Nome *</label>
                  <input
                    id="first_name"
                    type="text"
                    required
                    value={form.first_name}
                    onChange={(e) => update("first_name", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#3D2140]/10 text-[#3D2140] focus:outline-none focus:border-[#FF2768] focus:ring-2 focus:ring-[#FF2768]/20 transition"
                    data-testid="kit-input-firstname"
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-xs font-semibold text-[#3D2140] mb-1.5">Cognome *</label>
                  <input
                    id="last_name"
                    type="text"
                    required
                    value={form.last_name}
                    onChange={(e) => update("last_name", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#3D2140]/10 text-[#3D2140] focus:outline-none focus:border-[#FF2768] focus:ring-2 focus:ring-[#FF2768]/20 transition"
                    data-testid="kit-input-lastname"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-[#3D2140] mb-1.5">Email *</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#3D2140]/10 text-[#3D2140] focus:outline-none focus:border-[#FF2768] focus:ring-2 focus:ring-[#FF2768]/20 transition"
                  data-testid="kit-input-email"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs font-semibold text-[#3D2140] mb-1.5">Numero di telefono *</label>
                <input
                  id="phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#3D2140]/10 text-[#3D2140] focus:outline-none focus:border-[#FF2768] focus:ring-2 focus:ring-[#FF2768]/20 transition"
                  data-testid="kit-input-phone"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer group pt-2">
                <input
                  type="checkbox"
                  required
                  checked={form.privacy_accepted}
                  onChange={(e) => update("privacy_accepted", e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-[#3D2140]/30 text-[#FF2768] focus:ring-[#FF2768]/40 cursor-pointer accent-[#FF2768]"
                  data-testid="kit-input-privacy"
                />
                <span className="text-xs text-[#3D2140]/80 leading-relaxed">
                  Ho letto e accetto la <a href="/privacy" target="_blank" className="text-[#FF2768] underline hover:no-underline">Privacy Policy</a> e acconsento al trattamento dei miei dati per ricevere il materiale gratuito. *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  required
                  checked={form.marketing_consent}
                  onChange={(e) => update("marketing_consent", e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-[#3D2140]/30 text-[#FF2768] focus:ring-[#FF2768]/40 cursor-pointer accent-[#FF2768]"
                  data-testid="kit-input-marketing"
                />
                <span className="text-xs text-[#3D2140]/80 leading-relaxed">
                  Voglio ricevere il materiale gratuito e la newsletter di Teresa Vilardo con nuove risorse, spunti e aggiornamenti. Potrò disiscrivermi in qualsiasi momento con un click dal fondo di ogni email. *
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 inline-flex items-center justify-center gap-3 bg-[#FF2768] text-white rounded-full px-8 py-4 font-semibold text-base shadow-lg hover:shadow-xl hover:bg-[#E01F5A] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                data-testid="kit-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Invio in corso…
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    Ricevi la risorsa
                  </>
                )}
              </button>
            </form>
            </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
